import { useState, useCallback, useEffect } from "react";

/**
 * useDismissible (iter190) — hook per rendere qualsiasi banner "chiudibile".
 *
 * Salva in localStorage un timestamp fino al quale il banner resta nascosto
 * (default: 7 giorni). Se l'utente dismissa e il problema è ancora presente
 * dopo la scadenza, il banner riappare — non lo silenziamo per sempre.
 *
 * Usage:
 *   const [dismissed, dismiss, undismiss] = useDismissible("group-mismatch", 7);
 *   if (dismissed) return null;
 *   return <Banner> <X onClick={dismiss} /> </Banner>;
 */
export default function useDismissible(key, days = 7) {
  const storageKey = `banner_dismiss_${key}`;

  const isCurrentlyDismissed = () => {
    try {
      const val = localStorage.getItem(storageKey);
      if (!val) return false;
      const untilMs = parseInt(val, 10);
      if (!Number.isFinite(untilMs)) return false;
      return untilMs > Date.now();
    } catch {
      return false;
    }
  };

  const [dismissed, setDismissed] = useState(isCurrentlyDismissed);

  // Ricontrolla al mount (utile se altre parti della app dismissano lo stesso key)
  useEffect(() => {
    setDismissed(isCurrentlyDismissed());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const dismiss = useCallback(() => {
    try {
      const until = Date.now() + days * 24 * 60 * 60 * 1000;
      localStorage.setItem(storageKey, String(until));
      setDismissed(true);
    } catch { /* noop */ }
  }, [days, storageKey]);

  const undismiss = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
      setDismissed(false);
    } catch { /* noop */ }
  }, [storageKey]);

  return [dismissed, dismiss, undismiss];
}
